import { useEffect, useRef, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, AlertTriangle, Send, ShieldCheck, Flag, Ban } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import ReportDialog from "./ReportDialog";
import { blockUser } from "../lib/moderation";

/**
 * ChatPanel — in-app messaging between the current user and a peer about a
 * specific waste post or bin listing. Surfaces a permanent banner reminding
 * both parties that off-platform payments void buyer protection, and a live
 * inline warning when the server flags a message.
 *
 * Design notes (Feb 2026 native-fix rev):
 * - Report + Block are exposed as *inline* header buttons (were behind a
 *   Radix DropdownMenu). Rationale: the Radix portal was not reliably
 *   dispatching taps inside iOS WKWebView (Capacitor), so the reviewer
 *   couldn't reach the Guideline 1.2 controls on TestFlight. Inline
 *   buttons remove the portal + iOS pointer-capture surface entirely and
 *   also improve discoverability (Apple's reviewer sees them without
 *   needing to guess a hidden menu exists).
 * - `viewerRole` picks between customer (homeowner/business) and carrier
 *   quick replies. Previously ChatPanel hard-coded carrier phrases even
 *   for the homeowner viewer, which was jarring in the reviewer walk-through.
 * - Root Card is `overflow-hidden max-w-full` and message rows use
 *   `min-w-0` so any accidental long token wraps rather than pushing the
 *   viewport wider than the safe area on iOS.
 */
export default function ChatPanel({
  currentUserId,
  peerId,
  peerName,
  wastePostId,
  binId,
  reuseListingId,
  viewerRole,
  testIdPrefix = "chat",
}) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [lastWarning, setLastWarning] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const scrollRef = useRef(null);

  // Quick-reply suggestions. Which side of the conversation the viewer is
  // on matters — a homeowner sending "On my way 🚛" makes no sense.
  //   - Carriers get operational replies ("On my way", ETA changes)
  //   - Customers get coordinational replies (access details, thanks)
  // For bin listings the peer is always a bin renter of the bin owner, so
  // the split is by role too. Falls back to customer phrases if role is
  // unknown (defensive — never show carrier phrases to a non-carrier).
  const isCarrierView = viewerRole === "carrier";
  const QUICK_REPLIES = (() => {
    if (wastePostId) {
      return isCarrierView
        ? [
            "On my way 🚛",
            "Running 15 min late",
            "I'm at the gate",
            "Can I bring an extra van?",
            "All collected — see waste transfer note",
            "Thanks!",
          ]
        : [
            "Sounds good, thanks!",
            "What time will you arrive?",
            "The items are ready for collection",
            "Please buzz on arrival",
            "Can we reschedule?",
            "Thanks — see you then",
          ];
    }
    return isCarrierView
      ? [
          "On my way 🚛",
          "Running 15 min late",
          "I'm outside",
          "Bin's by the side gate",
          "Thanks!",
        ]
      : [
          "Sounds good, thanks!",
          "Bin's out by the side gate",
          "What time will you arrive?",
          "Please leave the lid down",
          "Thanks — see you then",
        ];
  })();

  const params = wastePostId
    ? { user_id: currentUserId, with_user_id: peerId, waste_post_id: wastePostId }
    : reuseListingId
      ? { user_id: currentUserId, with_user_id: peerId, reuse_listing_id: reuseListingId }
      : { user_id: currentUserId, with_user_id: peerId, bin_id: binId };

  const load = async () => {
    try {
      const { data } = await api.get("/messages", { params });
      setMessages(data);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      // silent — chat may be unavailable
    }
  };

  useEffect(() => {
    // `load` is a stable closure — including it in deps would fire the
    // effect every render. Silencing exhaustive-deps here intentionally.
    if (currentUserId && peerId && (wastePostId || binId || reuseListingId)) load();
  }, [currentUserId, peerId, wastePostId, binId, reuseListingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (e, overrideBody) => {
    e?.preventDefault?.();
    const text = (overrideBody ?? draft).trim();
    if (!text) return;
    setSending(true);
    setLastWarning(null);
    try {
      const payload = wastePostId
        ? { recipient_id: peerId, body: text, waste_post_id: wastePostId }
        : reuseListingId
          ? { recipient_id: peerId, body: text, reuse_listing_id: reuseListingId }
          : { recipient_id: peerId, body: text, bin_id: binId };
      const { data } = await api.post("/messages", payload, { params: { user_id: currentUserId } });
      if (data.warning) {
        setLastWarning(data.warning);
        toast.warning(data.warning);
      }
      if (!overrideBody) setDraft("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const sendQuick = (text) => send(null, text);

  const confirmBlock = async () => {
    try {
      await blockUser({ userId: currentUserId, blockedUserId: peerId });
      setBlocked(true);
      setBlockConfirmOpen(false);
      toast.success(`${peerName} has been blocked. They can no longer message you.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not block user");
    }
  };

  return (
    <Card
      data-testid={`${testIdPrefix}-panel`}
      className="rounded-2xl border-[#E2DFD8] p-0 overflow-hidden bg-white max-w-full"
    >
      {/* Header: title + inline Guideline-1.2 controls. Inline (rather
          than a hidden dropdown) so the reviewer/customer sees them at
          a glance and iOS WKWebView never has to render a portal. */}
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E2DFD8] bg-[#F6F4F0]">
        <div className="flex items-start gap-2 min-w-0">
          <MessageSquare className="w-5 h-5 text-[#4A6B56] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div
              className="font-medium text-[#1B3624] truncate"
              style={{ fontFamily: "Outfit" }}
            >
              Chat with {peerName}
            </div>
            <div className="text-[11px] text-[#6B6965]">
              All conversations are monitored for safety & T&Cs.
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid={`${testIdPrefix}-report`}
            onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E2DFD8] bg-white px-3 py-1.5 text-xs text-[#1B3624] hover:border-[#1B3624] active:bg-[#F6F4F0] min-h-[36px]"
          >
            <Flag className="w-3.5 h-3.5" /> Report user
          </button>
          <button
            type="button"
            data-testid={`${testIdPrefix}-block`}
            onClick={() => setBlockConfirmOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs text-red-700 hover:border-red-400 active:bg-red-50 min-h-[36px]"
          >
            <Ban className="w-3.5 h-3.5" /> Block user
          </button>
        </div>
      </div>

      <div
        data-testid={`${testIdPrefix}-permanent-warning`}
        className="px-4 sm:px-5 py-3 bg-[#FFF8F1] border-b border-[#F0E2D0] text-xs text-[#7A4A2A] flex items-start gap-2"
      >
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <strong>Stay on platform.</strong> Discussing cash, bank transfers, or off-platform contact details voids Binshare buyer/seller protection and breaches our Terms. Repeat violations may suspend your account.
        </div>
      </div>

      {lastWarning && (
        <div
          data-testid={`${testIdPrefix}-warning`}
          className="px-4 sm:px-5 py-3 bg-red-50 border-b border-red-100 text-xs text-red-800 flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="min-w-0">{lastWarning}</div>
        </div>
      )}

      <div
        ref={scrollRef}
        data-testid={`${testIdPrefix}-messages`}
        className="max-h-[360px] overflow-y-auto p-4 sm:p-5 space-y-2 bg-white"
      >
        {messages.length === 0 && (
          <div className="text-sm text-[#6B6965] text-center py-6">
            No messages yet — say hello!
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === currentUserId;
          return (
            <div
              key={m.id}
              data-testid={`${testIdPrefix}-msg-${m.id}`}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] min-w-0 rounded-2xl px-3 py-2 text-sm ${
                  mine ? "bg-[#1B3624] text-white" : "bg-[#F6F4F0] text-[#1A1A1A]"
                }`}
              >
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div
                  className={`mt-1 text-[10px] ${
                    mine ? "text-white/60" : "text-[#6B6965]"
                  } flex items-center gap-1`}
                >
                  {m.flagged && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                  {new Date(m.created_at).toLocaleString("en-GB", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        data-testid="quick-replies"
        className="flex gap-2 overflow-x-auto px-3 pt-3 pb-1 bg-[#FBFAF6] border-t border-[#E2DFD8] scrollbar-thin"
      >
        {QUICK_REPLIES.map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`quick-reply-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 30)}`}
            disabled={sending}
            onClick={() => sendQuick(t)}
            className="shrink-0 rounded-full border border-[#E2DFD8] hover:border-[#1B3624] hover:bg-[#1B3624] hover:text-white text-[#1A1A1A] text-xs px-3 py-1.5 transition disabled:opacity-50"
          >
            {t}
          </button>
        ))}
      </div>

      <form
        onSubmit={send}
        className="border-t border-[#E2DFD8] p-3 flex items-end gap-2 bg-[#FBFAF6]"
      >
        <Textarea
          data-testid={`${testIdPrefix}-input`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={`Message ${peerName}… (no cash / off-platform talk)`}
          className="rounded-2xl resize-none min-h-[44px] max-h-32 min-w-0 flex-1"
          rows={2}
        />
        <Button
          data-testid={`${testIdPrefix}-send`}
          type="submit"
          disabled={sending || !draft.trim() || blocked}
          className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-4 min-h-[44px] shrink-0"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>

      {blocked && (
        <div
          data-testid={`${testIdPrefix}-blocked-banner`}
          className="px-5 py-3 bg-red-50 border-t border-red-100 text-xs text-red-800"
        >
          You have blocked {peerName}. Neither of you can send new messages. Manage your blocks in Settings.
        </div>
      )}

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        userId={currentUserId}
        targetKind="user"
        targetId={peerId}
        targetLabel={peerName}
      />

      <AlertDialog open={blockConfirmOpen} onOpenChange={setBlockConfirmOpen}>
        <AlertDialogContent data-testid={`${testIdPrefix}-block-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Block {peerName}?</AlertDialogTitle>
            <AlertDialogDescription>
              They won&apos;t be able to message you and their existing messages will be hidden from your inbox. You can unblock them at any time from Settings → Blocked users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testIdPrefix}-block-cancel`}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`${testIdPrefix}-block-confirm-btn`}
              onClick={confirmBlock}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Block user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
